import Builder from './Builder'
import StaticModel from './StaticModel'
import { validateAll } from 'indicative'
import isEqual from 'lodash.isequal'
import cloneDeep from 'lodash.clonedeep'
import dataGet from 'lodash.get'

export default class Model extends StaticModel {
  constructor (...attributes) {
    super()

    this.inProgress = false
    this._attributes = {}
    this.$ = {}
    this.pagination = null
    this.errors = []
    this._without = []
    this._builder = new Builder(this.resource())

    Object.assign(this._attributes, this.defaults(), ...attributes)

    if (this.$http === undefined) {
      throw new Error('You must set $http property')
    }

    this.setup()
  }

  /**
   *  Setup
   */

  get $http () {
    return Model.$http
  }

  defaults () {
    return {}
  }

  validations () {
    return {}
  }

  async validate () {
    try {
      await validateAll(this._attributes, this.validations())
      this.errors = []
      return true
    } catch (errors) {
      this.errors = errors
      return false
    }
  }

  resource () {
    return `${this.constructor.name.toLowerCase()}s`
  }

  primaryKey () {
    return 'id'
  }

  getPrimaryKey () {
    return this[this.primaryKey()]
  }

  baseURL () {
    return ''
  }

  async request (config) {
    this.inProgress = true

    if (config.method !== 'GET') {
      config.force = true
    }

    try {
      const response = await this.$http.request(config)
      this.inProgress = false

      return this.parse(response)
    } catch (error) {
      this.inProgress = false

      if (error.response && error.response.status === 422) {
        const errors = (error.response.data && error.response.data.errors) || {}

        Object.keys(errors).forEach(key => {
          this.errors.push({
            field: key,
            message: errors[key]
          })
        })
      }
      throw error
    }
  }

  custom (...args) {
    if (args.length === 0) {
      throw new Error('The custom() method takes a minimum of one argument.')
    }

    // It would be unintuitive for users to manage where the '/' has to be for
    // multiple arguments. We don't need it for the first argument if it's
    // a string, but subsequent string arguments need the '/' at the beginning.
    // We handle this implementation detail here to simplify the readme.
    let slash = ''
    let resource = ''

    args.forEach(value => {
      switch (true) {
        case (typeof value === 'string'):
          resource += slash + value.replace(/^\/+/, '')
          break
        case (value instanceof Model):
          resource += slash + value.resource()

          if (value.isValidId(value.getPrimaryKey())) {
            resource += '/' + value.getPrimaryKey()
          }
          break
        default:
          throw new Error('Arguments to custom() must be strings or instances of Model.')
      }

      if (!slash.length) {
        slash = '/'
      }
    })

    this._customResource = resource

    return this
  }

  hasMany (model) {
    /* eslint-disable new-cap */
    let instance = new model()
    let url = `${this.baseURL()}/${this.resource()}/${this.getPrimaryKey()}/${instance.resource()}`

    instance._from(url)

    return instance
  }

  _from (url) {
    Object.defineProperty(this, '_fromResource', { get: () => url })
    return this
  }

  for (...args) {
    if (args.length === 0) {
      throw new Error('The for() method takes a minimum of one argument.')
    }

    let url = `${this.baseURL()}`

    args.forEach(object => {
      if (object instanceof Model === false) {
        throw new Error('The object referenced on for() method is not a valid Model.')
      }

      if (!this.isValidId(object.getPrimaryKey())) {
        throw new Error('The object referenced on for() method has a invalid id.')
      }

      url += `/${object.resource()}/${object.getPrimaryKey()}`
    })

    url += `/${this.resource()}`

    this._from(url)

    return this
  }

  /**
   * Helpers
   */

  defineAttrs (prop) {
    Object.defineProperty(this, prop,
      {
        set (value) {
          this._attributes[prop] = value
          this.isChanged = !isEqual(this._attributes, this.$)
          this.validate()
        },
        get () {
          return this._attributes[prop]
        },
        enumerable: true,
        configurable: true
      })
  }

  without (args) {
    this._without.push(...args)
    return this
  }

  setup () {
    this.$ = cloneDeep(this._attributes)
    this.isChanged = false
    Object.keys(this._attributes).forEach(key => {
      this.defineAttrs(key)
    })

    this._without = []
  }

  isDirty (...attributes) {
    if (attributes.length === 0) {
      return !isEqual(this._attributes, this.$)
    }

    let dirty = true
    attributes.forEach(key => {
      if (isEqual(dataGet(this._attributes, key), dataGet(this.$, key))) {
        dirty = false
      }
    })

    return dirty
  }

  reset () {
    this._attributes = cloneDeep(this.$)
  }

  hasId () {
    const id = this.getPrimaryKey()
    return this.isValidId(id)
  }

  isValidId (id) {
    return id !== undefined && id !== 0 && id !== ''
  }

  endpoint () {
    if (this._fromResource) {
      if (this.hasId()) {
        return `${this._fromResource}/${this.getPrimaryKey()}`
      } else {
        return this._fromResource
      }
    }

    if (this.hasId()) {
      return `${this.baseURL()}/${this.resource()}/${this.getPrimaryKey()}`
    } else {
      return `${this.baseURL()}/${this.resource()}`
    }
  }

  payload () {
    return Object.keys(this._attributes)
      .filter(x => !this._without.includes(x))
      .reduce((obj, key) => {
        obj[key] = this._attributes[key]
        return obj
      }, {})
  }

  getChangedDefaultAttributes () {
    let changed = []
    Object.keys(this.defaults()).forEach(key => {
      if (this._attributes[key] !== this.$[key]) {
        changed.push(key)
      }
    })

    return changed
  }

  loadFilters (filters) {
    if (!filters || filters.length === 0) {
      return this
    }

    let self = this

    filters.forEach(function (filter, index) {
      if (filter.type !== 'date') {
        return self.where(filter.field, filter.operator, filter.value)
      }

      if (filter.from) {
        self.where(filter.field, 'gte', filter.from)
      }

      if (filter.to) {
        self.where(filter.field, 'lte', filter.to)
      }
    })

    return self
  }

  /**
   *  Query
   */

  include (...args) {
    this._builder.include(...args)
    return this
  }

  append (...args) {
    this._builder.append(...args)
    return this
  }

  select (...fields) {
    this._builder.select(...fields)
    return this
  }

  where (field, operator, value) {
    this._builder.where(field, operator, value)
    return this
  }

  whereIn (field, array) {
    this._builder.whereIn(field, array)
    return this
  }

  orderBy (...args) {
    this._builder.orderBy(...args)
    return this
  }

  page (value) {
    this._builder.page(value)
    return this
  }

  limit (value) {
    this._builder.limit(value)
    return this
  }

  params (payload) {
    this._builder.params(payload)
    return this
  }

  /**
   * Result
   */

  async first () {
    const { data } = await this
      .limit(1)
      .get()

    return new this.constructor(data)
  }

  async find (identifier, config = {}) {
    if (identifier === undefined) {
      throw new Error('You must specify the param on find() method.')
    }
    let base = this._fromResource || `${this.baseURL()}/${this.resource()}`
    let url = `${base}/${identifier}${this._builder.query()}`

    config.url = url
    config.method = 'GET'

    const { data } = await this.request(config)
    return new this.constructor(data)
  }

  async refresh () {
    let base = this._fromResource || `${this.baseURL()}/${this.resource()}`
    let url = `${base}/${this._attributes[this.primaryKey()]}${this._builder.query()}`

    const { data } = await this.request({
      url,
      method: 'GET'
    })

    Object.assign(this._attributes, this.defaults(), data)
    this.setup()
  }

  fetch (cb) {
    const poll = (page, resolve) => {
      this
        .page(page)
        .get(false)
        .then(data => {
          cb(data)

          if (page < this.pagination.last_page) {
            poll(page + 1, resolve)
          } else {
            resolve()
          }
        })
    }

    return new Promise(resolve => {
      poll(1, resolve)
    }).then(() => {
      // reset page
      this.page(1)
    })
  }

  async get (updatePage = true, config = {}) {
    let base = this._fromResource || `${this.baseURL()}/${this.resource()}`
    base = this._customResource ? `${this.baseURL()}/${this._customResource}` : base

    const url = this._builder ? `${base}${this._builder.query()}` : this.endpoint()

    config.url = url
    config.method = 'Get'
    config.updatePage = updatePage

    const { data } = await this.request(config)

    const response = Array.isArray(data) ? data : [data]
    return response.map(c => new this.constructor(c)._from(this._fromResource))
  }

  async getFullData (config = {}) {
    this.inProgress = true
    let response = {}
    let base = this._fromResource || `${this.baseURL()}/${this.resource()}`
    base = this._customResource ? `${this.baseURL()}/${this._customResource}` : base

    const url = this._builder ? `${base}${this._builder.query()}` : this.endpoint()

    config.url = url
    config.method = 'GET'

    try {
      response = await this.$http.request(config)
      this.inProgress = false
    } catch (error) {
      this.inProgress = false

      if (error.response && error.response.status === 422) {
        const errors = (error.response.data && error.response.data.errors) || {}

        Object.keys(errors).forEach(key => {
          this.errors.push({
            field: key,
            message: errors[key]
          })
        })
      }
      throw error
    }
    const collectionData = Array.isArray(response.data.data) ? response.data.data : [response.data.data]

    return {
      collection: collectionData.map(c => new this.constructor(c)._from(this._fromResource)),
      page: response.data.meta
    }
  }

  /**
   * Common CRUD operations
   */

  delete (config = {}) {
    if (!this.hasId()) {
      throw new Error('This model has a empty ID.')
    }

    config.url = this.endpoint()
    config.method = 'DELETE'

    return this.request(config).then(() => { this.setup() })
  }

  // TODO: all others? feathers/backbone?
  updateMethod () {
    return 'PUT'
  }

  routes () {
    return {
      delete: {
        method: '',
        path: ''
      }
    }
  }

  parse (request) {
    return {
      data: request.data
    }
  }

  async save (config = {}) {
    const isValid = await this.validate()

    if (!isValid) {
      throw new Error('Validation failed')
    }

    const { data } = this.hasId() ? await this._update(config) : await this._create(config)
    this._attributes = Object.assign(this._attributes, data)
    this.setup()
    return this
  }

  _create (config = {}) {
    return this.request({
      ...config,
      method: 'POST',
      url: this.endpoint(),
      data: this.payload()
    })
  }

  _update (config = {}) {
    return this.request({
      ...config,
      method: this.updateMethod(),
      url: this.endpoint(),
      data: this.payload()
    })
  }

  /**
   * Relationship operations
   */

  attach (params) {
    return this.request({
      method: 'POST',
      url: this.endpoint(),
      data: params
    }).then(response => response.data)
  }

  sync (params) {
    return this.request({
      method: 'PUT',
      url: this.endpoint(),
      data: params
    }).then(response => response.data)
  }
}
